import { Fragment, useCallback, useMemo, useState } from 'react';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { DataSyncIcon, DataSyncLeftIcon, DataSyncRightIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import UISection from 'UIComponents/section/UISection';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UILink from 'UIComponents/link/UILink';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import * as Sync from '../constants/dataSync/Sync';
import SharedDataCard from './SharedDataCard';
import { getDataSyncProductUrl } from '../../common/utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EmptyComponent = () => {
  return null;
};
const SyncDirectionIcon = ({
  sync
}) => {
  let renderedIcon;
  switch (sync) {
    case Sync.INBOUND:
      renderedIcon = /*#__PURE__*/_jsx(IconHolder, {
        color: "fill-primary-default",
        icon: DataSyncRightIcon,
        size: "600"
      });
      break;
    case Sync.OUTBOUND:
      renderedIcon = /*#__PURE__*/_jsx(IconHolder, {
        color: "fill-primary-default",
        icon: DataSyncLeftIcon,
        size: "600"
      });
      break;
    case Sync.BOTH:
      renderedIcon = /*#__PURE__*/_jsx(IconHolder, {
        color: "fill-primary-default",
        icon: DataSyncIcon,
        size: "600"
      });
      break;
    default:
      renderedIcon = null;
      break;
  }
  return renderedIcon;
};
function SharedData({
  dataSyncHowItWorks,
  dataSyncInfo,
  dataSyncs,
  iconUrl,
  name,
  onTrackInteraction,
  opsHub
}) {
  const [badgeHovered, setBadgeHovered] = useState(false);
  const trackInteraction = useCallback(action => onTrackInteraction(action, {
    section: 'overview'
  }), [onTrackInteraction]);
  const renderedDataSyncInfo = dataSyncInfo && !opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-ui-apps-core.detail.overview.sharedData.moreInfoText`
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(UIBreakString, {
        children: dataSyncInfo
      })
    })]
  }) : null;
  const renderedHowItWorks = dataSyncHowItWorks && opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      marginBlockEnd: "100",
      as: "p",
      children: /*#__PURE__*/_jsx("b", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-apps-core.detail.overview.sharedData.howItWorks`
        })
      })
    }), /*#__PURE__*/_jsx(UIBreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: dataSyncHowItWorks
      })
    })]
  }) : null;
  const renderedDataSyncs = useMemo(() => {
    return /*#__PURE__*/_jsx("div", {
      children: dataSyncs.map((dataSync, index) => /*#__PURE__*/_jsx(SharedDataCard, {
        dataSync: dataSync,
        iconUrl: iconUrl,
        name: name,
        onToggleOpen: () => trackInteraction('toggle field mapping section open'),
        onOpenToolTip: () => trackInteraction('hover shared data description'),
        opsHub: opsHub,
        SyncIcon: SyncDirectionIcon
      }, `data-sync-card-${index}`))
    });
  }, [dataSyncs, iconUrl, name, opsHub, trackInteraction]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [opsHub && /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "500",
      children: /*#__PURE__*/_jsx(UILink, {
        href: getDataSyncProductUrl(getLang()),
        onClick: () => trackInteraction('click data sync badge'),
        external: true,
        ExternalIcon: EmptyComponent,
        children: /*#__PURE__*/_jsx(Badge, {
          onMouseEnter: () => setBadgeHovered(true),
          onMouseLeave: () => setBadgeHovered(false),
          use: badgeHovered ? 'pantera' : 'koala',
          children: /*#__PURE__*/_jsx(View, {
            paddingBlockStart: "100",
            children: /*#__PURE__*/_jsx(UIMediaObject, {
              itemLeft: /*#__PURE__*/_jsx(IconHolder, {
                icon: DataSyncIcon,
                size: "300",
                color: "fill-primary-default",
                marginBlockStart: "50",
                marginInlineEnd: "50",
                padding: "0"
              }),
              spacing: "none",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `marketplace-ui-apps-core.detail.overview.sharedData.badge`
              })
            })
          })
        })
      })
    }), renderedHowItWorks, /*#__PURE__*/_jsx(UISection, {
      children: renderedDataSyncs
    }), renderedDataSyncInfo]
  });
}
export default SharedData;